
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

// Add this to extend the jsPDF type with autoTable
declare module "jspdf" {
  interface jsPDF {
    autoTable: (options: any) => jsPDF;
    lastAutoTable: {
      finalY: number;
    };
  }
}

export const generateNutritionPDF = async (recommendation: any) => {
  try {
    // Obter informações do personal trainer
    const { data: trainerData, error: trainerError } = await supabase
      .from("profiles")
      .select("first_name, last_name")
      .eq("id", recommendation.trainer_id)
      .single();

    if (trainerError) {
      console.error("Erro ao buscar dados do personal:", trainerError);
    }

    // Create PDF document
    const doc = new jsPDF();
    
    // Add title
    doc.setFontSize(20);
    doc.setTextColor("#9B87F5");
    doc.text("Recomendação Nutricional", 20, 20);

    // Adicionar informações do cabeçalho em formato de tabela
    const headerData = [];
    
    // Informações do aluno
    headerData.push(['Aluno', `${recommendation.students.first_name} ${recommendation.students.last_name}`]);
    
    // Adicionar nome do personal se disponível
    if (trainerData) {
      headerData.push(['Personal Trainer', `${trainerData.first_name} ${trainerData.last_name}`]);
    }
    
    // Nível de atividade
    headerData.push(['Nível de Atividade', recommendation.activity_level.replace('_', ' ')]);
    
    // Adicionar tabela de informações
    doc.autoTable({
      startY: 30,
      head: [],
      body: headerData,
      theme: 'plain',
      styles: {
        fontSize: 11,
        cellPadding: 3,
      },
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 40 },
        1: { cellWidth: 'auto' }
      }
    });
    
    // Add macros table
    doc.autoTable({
      startY: doc.lastAutoTable.finalY + 15,
      head: [["Macronutriente", "Quantidade"]],
      body: [
        ["Calorias", `${recommendation.calories} kcal`],
        ["Proteínas", `${recommendation.protein}g`],
        ["Carboidratos", `${recommendation.carbs}g`],
        ["Gorduras", `${recommendation.fats}g`],
      ],
      theme: 'grid',
      styles: {
        fontSize: 10,
        cellPadding: 5,
      },
      headStyles: {
        fillColor: "#9B87F5",
        textColor: "#FFFFFF",
        fontStyle: 'bold'
      },
    });

    let currentY = doc.lastAutoTable.finalY + 20;

    // Add food suggestions
    if (recommendation.food_suggestions) {
      doc.setFontSize(14);
      doc.setTextColor("#333333");
      doc.text("Sugestões de Alimentos", 20, currentY);
      currentY += 10;
      
      const suggestions = recommendation.food_suggestions.split('\n\n');
      const suggestionRows = suggestions.map(suggestion => [suggestion]);
      
      doc.autoTable({
        startY: currentY,
        head: [],
        body: suggestionRows,
        theme: 'plain',
        styles: {
          fontSize: 10,
          cellPadding: 5
        }
      });
      
      currentY = doc.lastAutoTable.finalY + 15;
    }

    // Add disclaimer
    doc.setFontSize(10);
    doc.setTextColor("#666666");
    
    const disclaimer = "Atenção: As informações fornecidas aqui são apenas para fins educativos e informativos. Estas sugestões nutricionais são genéricas e não devem ser consideradas como aconselhamento nutricional personalizado. É fundamental consultar um nutricionista ou dietista qualificado para obter um plano alimentar individualizado e adaptado às suas necessidades específicas, condições de saúde e objetivos.";
    
    // Split disclaimer text into multiple lines
    const splitDisclaimer = doc.splitTextToSize(disclaimer, 170);
    
    // Add disclaimer in a box
    doc.autoTable({
      startY: currentY,
      head: [],
      body: [[splitDisclaimer]],
      theme: 'plain',
      styles: {
        fontSize: 8,
        cellPadding: 5,
        textColor: "#666666",
        fontStyle: 'italic'
      },
      tableLineColor: "#CCCCCC",
      tableLineWidth: 0.1,
      didDrawCell: function(data) {
        doc.setDrawColor("#CCCCCC");
        doc.rect(data.cell.x, data.cell.y, data.cell.width, data.cell.height, 'S');
      }
    });

    // Adicionar notas se disponíveis
    if (recommendation.notes) {
      doc.setFontSize(14);
      doc.setTextColor("#333333");
      doc.text("Observações", 20, currentY);
      currentY += 10;
      
      const notesRows = [[recommendation.notes]];
      
      doc.autoTable({
        startY: currentY,
        head: [],
        body: notesRows,
        theme: 'plain',
        styles: {
          fontSize: 10,
          cellPadding: 5,
          fontStyle: 'italic'
        }
      });
    }

    // Save the PDF
    const fileName = `recomendacao-nutricional-${recommendation.students.first_name.toLowerCase()}-${recommendation.students.last_name.toLowerCase()}.pdf`;
    doc.save(fileName);
    
    // Notificar o usuário sobre o sucesso
    toast.success("PDF de nutrição gerado com sucesso!");
  } catch (error) {
    console.error("Erro ao gerar PDF:", error);
    toast.error("Erro ao gerar o PDF de nutrição. Por favor, tente novamente.");
    throw error;
  }
};
